import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Search, TrendingUp, CheckCircle2, XCircle, MapPin, User, Euro } from "lucide-react";
import PipelineDetail from "../components/pipeline/PipelineDetail";

const money = (v) => "€ " + (Number(v || 0)).toLocaleString("it-IT");

export default function Pipeline() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [overview, setOverview] = useState(null);
  const [stato, setStato] = useState("all");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", zone: "", ref_name: "", ref_surname: "", ref_phone: "", ref_email: "" });

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (stato !== "all") p.set("stato", stato);
    if (q) p.set("q", q);
    api.get(`/pipeline?${p.toString()}`).then((r) => setItems(r.data)).catch(() => {});
    api.get("/pipeline/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, [stato, q]);

  useEffect(() => { api.get("/pipeline/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("open");
    if (p) { setSelectedId(p); window.history.replaceState({}, "", "/pipeline"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const statoMeta = (key) => (meta?.stati || []).find((s) => s.key === key) || { label: key, color: "#64748b" };

  const create = async () => {
    if (!form.name && !form.address) return toast.error("Inserire nome o indirizzo");
    if (!form.ref_name) return toast.error("Nome referente obbligatorio");
    try {
      const r = await api.post("/pipeline", form);
      toast.success("Opportunità creata");
      setShowNew(false);
      setForm({ name: "", address: "", zone: "", ref_name: "", ref_surname: "", ref_phone: "", ref_email: "" });
      setSelectedId(r.data.id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  if (selectedId) {
    return <PipelineDetail id={selectedId} meta={meta} onBack={() => { setSelectedId(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-indigo-600"><Building2 size={16} /> Appartamenti in Preparazione / Trattativa</div>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Pipeline commerciale degli immobili non ancora attivi. Non compaiono in Calendario, Prenotazioni, QR, Portale Proprietario, Report o Pagamenti finché non vengono convertiti.</p>
        </div>
        <Button className="gap-2" onClick={() => setShowNew(true)} data-testid="pipeline-new-btn"><Plus size={15} /> Nuova opportunità</Button>
      </div>

      {overview && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Card className="border-slate-200 p-4" data-testid="pipeline-kpi-active"><div className="flex items-center gap-2 text-xs text-slate-500"><TrendingUp size={14} /> Attive</div><div className="mt-1 text-2xl font-semibold text-slate-800">{overview.active}</div></Card>
          <Card className="border-slate-200 p-4"><div className="flex items-center gap-2 text-xs text-slate-500"><Building2 size={14} /> Totali</div><div className="mt-1 text-2xl font-semibold text-slate-800">{overview.total}</div></Card>
          <Card className="border-slate-200 p-4"><div className="flex items-center gap-2 text-xs text-emerald-600"><CheckCircle2 size={14} /> Convertite</div><div className="mt-1 text-2xl font-semibold text-emerald-700">{overview.converted}</div></Card>
          <Card className="border-slate-200 p-4"><div className="flex items-center gap-2 text-xs text-rose-600"><XCircle size={14} /> Scartate</div><div className="mt-1 text-2xl font-semibold text-rose-700">{overview.discarded}</div></Card>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca immobile / referente…" className="h-9 w-64 pl-8" data-testid="pipeline-search" />
        </div>
        <Select value={stato} onValueChange={setStato}>
          <SelectTrigger className="h-9 w-56" data-testid="pipeline-filter-stato"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {(meta?.stati || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {items.length === 0 ? (
        <Card className="border-dashed border-slate-200 p-10 text-center text-sm text-slate-400" data-testid="pipeline-empty">Nessuna opportunità. Crea la prima con "Nuova opportunità".</Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {items.map((it) => {
            const sm = statoMeta(it.stato);
            return (
              <Card key={it.id} className="cursor-pointer border-slate-200 p-4 transition-shadow hover:shadow-md" onClick={() => setSelectedId(it.id)} data-testid={`pipeline-card-${it.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-slate-800">{it.name || it.address || "Senza nome"}</div>
                    {it.address && <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-500"><MapPin size={12} /> {it.address}{it.zone ? ` · ${it.zone}` : ""}</div>}
                  </div>
                  <span className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium text-white" style={{ backgroundColor: sm.color }}>{sm.label}</span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
                  <span className="flex items-center gap-1"><User size={12} /> {[it.ref_name, it.ref_surname].filter(Boolean).join(" ") || "—"}</span>
                  {it.canone_richiesto > 0 && <span className="flex items-center gap-1"><Euro size={12} /> {money(it.canone_richiesto)}/mese</span>}
                  {it.commission_pct > 0 && <span className="text-slate-400">Comm. {it.commission_pct}%</span>}
                </div>
                {it.converted_apartment_id && <div className="mt-2 text-[11px] font-medium text-emerald-600">🏠 Convertito in appartamento</div>}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent data-testid="pipeline-new-dialog">
          <DialogHeader>
            <DialogTitle>Nuova opportunità</DialogTitle>
            <DialogDescription>Inserisci i dati minimi. Potrai completare la scheda dopo.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input placeholder="Nome immobile" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pn-name" />
            <Input placeholder="Zona" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} />
            <Input placeholder="Indirizzo" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="sm:col-span-2" data-testid="pn-address" />
            <div className="sm:col-span-2 border-t pt-2 text-xs font-semibold uppercase text-slate-400">Referente (obbligatorio)</div>
            <Input placeholder="Nome referente *" value={form.ref_name} onChange={(e) => setForm({ ...form, ref_name: e.target.value })} data-testid="pn-refname" />
            <Input placeholder="Cognome" value={form.ref_surname} onChange={(e) => setForm({ ...form, ref_surname: e.target.value })} />
            <Input placeholder="Telefono" value={form.ref_phone} onChange={(e) => setForm({ ...form, ref_phone: e.target.value })} />
            <Input placeholder="Email" value={form.ref_email} onChange={(e) => setForm({ ...form, ref_email: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Annulla</Button>
            <Button onClick={create} data-testid="pn-save">Crea opportunità</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
